import ReactGA from "react-ga4";

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
let gaInitialized = false;

const initGAOnce = () => {
  if (gaInitialized || !GA_MEASUREMENT_ID) return;
  gaInitialized = true;
  ReactGA.initialize(GA_MEASUREMENT_ID);
};

/** Defer GA until first user interaction or after 4s to reduce impact on FCP/LCP */
export const initGA = () => {
  if (!GA_MEASUREMENT_ID) return;
  const defer = () => {
    initGAOnce();
    ["click", "keydown", "scroll"].forEach((ev) =>
      window.removeEventListener(ev, defer)
    );
    clearTimeout(timer);
  };
  ["click", "keydown", "scroll"].forEach((ev) =>
    window.addEventListener(ev, defer, { once: true, passive: true })
  );
  const timer = setTimeout(initGAOnce, 4000);
};

export const logPageView = () => {
  if (GA_MEASUREMENT_ID) {
    ReactGA.send({ hitType: "pageview", page: window.location.pathname + window.location.search });
  }
};

export const logEvent = (category: string, action: string, label?: string) => {
  if (GA_MEASUREMENT_ID) {
    ReactGA.event({ category, action, label });
  }
};

// ── Performance Monitoring ──────────────────────────────────────────

/**
 * Measure Web Vitals (LCP, FID, CLS, FCP, TTFB) and send to GA4.
 * Call once on app mount.
 */
export const measureWebVitals = () => {
  if (!('PerformanceObserver' in window)) return;

  // Largest Contentful Paint
  try {
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      sendPerfEvent('LCP', Math.round(lastEntry.startTime));
    });
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch { /* unsupported */ }

  // First Input Delay
  try {
    const fidObserver = new PerformanceObserver((list) => {
      const entry = list.getEntries()[0] as PerformanceEventTiming;
      sendPerfEvent('FID', Math.round(entry.processingStart - entry.startTime));
    });
    fidObserver.observe({ type: 'first-input', buffered: true });
  } catch { /* unsupported */ }

  // Cumulative Layout Shift
  try {
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!(entry as any).hadRecentInput) {
          clsValue += (entry as any).value;
        }
      }
    });
    clsObserver.observe({ type: 'layout-shift', buffered: true });
    // Report on page hide
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        sendPerfEvent('CLS', Math.round(clsValue * 1000)); // multiply for readability
      }
    }, { once: true });
  } catch { /* unsupported */ }

  // First Contentful Paint
  try {
    const paintEntries = performance.getEntriesByType('paint');
    const fcp = paintEntries.find(e => e.name === 'first-contentful-paint');
    if (fcp) sendPerfEvent('FCP', Math.round(fcp.startTime));
  } catch { /* unsupported */ }

  // Navigation timing (TTFB)
  try {
    const [nav] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    if (nav) sendPerfEvent('TTFB', Math.round(nav.responseStart - nav.requestStart));
  } catch { /* unsupported */ }
};

const sendPerfEvent = (metric: string, value: number) => {
  if (!GA_MEASUREMENT_ID) return;
  ReactGA.event({
    category: 'Web Vitals',
    action: metric,
    value,
    nonInteraction: true,
    label: window.location.pathname,
  });
};

// ── User Behavior Tracking ──────────────────────────────────────────

export const trackEvent = (
  eventName: string,
  params?: Record<string, string | number | boolean | undefined>
) => {
  if (!GA_MEASUREMENT_ID) return;
  // Filter out undefined values
  const cleanParams = params
    ? Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined))
    : {};
  ReactGA.event({ category: 'User Behavior', action: eventName, ...cleanParams });
};

// Pre-built tracking helpers for key business events
export const trackSearch = (query: string, filtersCount: number, resultsCount: number) =>
  trackEvent('search_performed', { label: query, value: resultsCount, filters_count: filtersCount });

export const trackHostelView = (hostelId: string, source: string) =>
  trackEvent('hostel_view', { label: hostelId, source });

export const trackBookingStarted = (hostelId: string, price: number) =>
  trackEvent('booking_started', { label: hostelId, value: price });

export const trackBookingCompleted = (bookingId: string, value: number) =>
  trackEvent('booking_completed', { label: bookingId, value });

export const trackPaymentFailed = (errorCode: string, amount: number) =>
  trackEvent('payment_failed', { label: errorCode, value: amount });
